import './Companies.css';
import React, { useState } from 'react';
import { Container } from '../../components/Container/Container';
import { Card } from '../../components/Card/Card';
import { Icon } from '../../components/Icon/Icon';
import { Pagination } from '../../components/Pagination/Pagination';
import { SortableList } from '../../components/SortableList/SortableList';
import { usePaginatedResponse, useData } from '../../model/data';
import { getCompanies, CompanyBase } from '../../model/company';
import { CompanyForm } from './CompaniesModal/CompanyForm';
import { companyPath, companyPayoutsPath, orderPath } from '../../routes';
import { Avatar } from '../../components/Avatar/Avatar';
import { useMeta } from '../../model/meta';
import { useQueryAsState } from '../../model/history';
import { FilterBar } from '../../components/FilterBar/FilterBar';
import { t } from '../../locales/translate';
import { formatDateTime } from '../../util/util';
import { NavLink } from 'react-router-dom';
import { TableBlock } from '../../components/TableBlock/TableBlock';
import { compact } from 'lodash';
import { SortByIcon } from '../../components/SortableList/SortByIcon';
import { Modal } from '../../components/Modal/Modal';
import { ExternalLink } from '../../components/ExternalLink/ExternalLink';

export function Companies(): JSX.Element {
  // HOOKS
  const [showAddModal, setShowAddModal] = useState(false);
  const [params] = useQueryAsState();

  const paginator = usePaginatedResponse(
    (page, perPage) =>
      getCompanies({
        page,
        perPage,
        query: params.query,
        sortBy: params.sortBy,
        sortDir: params.sortDir,
      }),
    JSON.stringify(params)
  );
  useMeta({ title: t.model.company._plural });
  const companies = useData((db) => compact(paginator.currentIds.map((e) => db.table('companies').find(e))));
  // METHODS
  const addCompanyButton = (
    <span
      onClick={() => {
        setShowAddModal(true);
      }}
    >
      <Icon name="plus" />
    </span>
  );
  return (
    <Container className="companies">
      <Card
        className="companies__list"
        title={t.model.company._plural}
        elevation={2}
        actions={addCompanyButton}
        pagination={<Pagination {...paginator} />}
        fullbleed
      >
        <SortableList
          items={companies}
          error={paginator.error}
          actions={<FilterBar filters={['query']} />}
          loading={paginator.loading}
          columns={[
            {
              width: '40px',
              sortable: false,
              dataKey: 'icon',
              cellRenderer: (row) => {
                return <Avatar image={row.icon && row.icon.url} name={row.name || ''} size="large" />;
              },
            },
            {
              label: t.general.name,
              apiSortIcon: <SortByIcon sortBy="name" />,
              dataKey: 'name',
            },
            {
              label: t.general.slug,
              apiSortIcon: <SortByIcon sortBy="slug" />,
              dataKey: 'slug',
            },
            {
              label: t.model.invoice.invoicingCycle,
              apiSortIcon: <SortByIcon sortBy="invoicing_cycle" />,
              dataKey: 'invoicingCycle',
            },
            {
              label: t.model.invoice.lastClearingInvoice,
              apiSortIcon: <SortByIcon sortBy="last_booked_clearing_invoice_booked_at" />,
              dataKey: 'lastBookedClearingInvoice',
              cellRenderer: (row) =>
                row.lastBookedClearingInvoice && (
                  <NavLink to={orderPath(row.lastBookedClearingInvoice.orderId)}>
                    <TableBlock
                      header={row.lastBookedClearingInvoice.reference}
                      footer={formatDateTime(row.lastBookedClearingInvoice?.bookedAt)}
                    />
                  </NavLink>
                ),
            },
            {
              label: t.model.invoice.eventPayoutCycle,
              sortable: false,
              dataKey: 'eventPayoutCycle',
              disableLink: true,
              cellRenderer: (row) =>
                row.eventPayoutCycle && (
                  <>
                    {t.model.invoice.invoicingCycles[row.eventPayoutCycle]}
                    <ExternalLink url={companyPayoutsPath(row)} />
                  </>
                ),
            },
            {
              label: t.model.invoice.lastEventPayout,
              sortable: false,
              dataKey: 'lastEventPayout',
              cellRenderer: (row) => row.lastEventPayout && formatDateTime(row.lastEventPayout),
            },
          ]}
          rowLink={(row) => companyPath(row)}
        />
      </Card>
      <Modal open={showAddModal} title={t.actions.add(t.model.company._name)} size="small">
        <CompanyForm company={{} as CompanyBase} onClose={() => setShowAddModal(false)} />
      </Modal>
    </Container>
  );
}
